<template>
  <div>
    <div class="push-configuration live-widgets-configuration">
      <h1 v-if="hasWidgetPackage">
        Live Widgets Configuration
      </h1>

      <template v-if="hasWidgetPackage">
        <div class="container">
          <div class="align-left">
            <template v-if="!initialLoading && accessKey">
              <WidgetEndpoints
                :endpoints="endpoints"
                :loading="loading"
                :error="error"
                :has-access-key="true"
                @save="saveAndGenerate"
                @cancel="cancelConfigure"
              />
            </template>
            <template v-else-if="!initialLoading">
              <div class="generate-key-card">
                <h2 class="configure-title">
                  <svg>
                    <use v-bind="{'xlink:href':'#settings-icon'}" />
                  </svg>
                  Configure
                </h2>
                <div class="generate-key-card__body">
                  <p class="generate-key-card__desc">
                    Generate your Public Key to get started.
                    You can add your allowed domains (endpoints)
                    after the key is generated.
                  </p>
                  <p v-if="error" class="error-message">
                    {{ error }}
                  </p>
                  <button
                    class="btn-style-common medium"
                    :disabled="loading"
                    @click="generatePublicKey"
                  >
                    {{ loading ? 'Generating...' : 'Generate Public Key' }}
                  </button>
                </div>
              </div>
            </template>
          </div>
          <div class="align-right">
            <WidgetCredentials
              :project-key="activeProject
                ? activeProject.key : ''"
              :api-key="activeApiKey"
              :access-key="accessKey"
            />
          </div>
        </div>

        <WidgetIntegrationGuide
          :publishable-key="accessKey"
        />
      </template>

      <div v-else class="no-access">
        <div class="no-access__card">
          <div class="no-access__content">
            <h2 class="no-access__heading">
              Cricket Widget Package Required
            </h2>
            <p class="no-access__desc">
              You need the <strong>Cricket Widget</strong>
              package to configure and embed live cricket
              widgets on your website. Get started with a
              free trial or purchase the package to unlock
              live scores, scorecard, tournament browser,
              and more.
            </p>
            <div class="no-access__actions">
              <a
                href="https://www.cricketapi.com/v5/cricket-widget"
                target="_blank"
                rel="noopener"
                class="btn-style-common medium"
              >Learn More</a>
            </div>
          </div>
          <div class="no-access__image">
            <img
              src="https://assets.sports.dev.roanuz.com/uploads-v5/cricket/website/banners/16mar2026/Scorecard-s3.png"
              alt="Cricket Widget Preview"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import Config from '@/config';
import WidgetEndpoints from '@/components/widget-config/WidgetEndpoints.vue';
import WidgetCredentials from '@/components/widget-config/WidgetCredentials.vue';
import WidgetIntegrationGuide from '@/components/widget-config/WidgetIntegrationGuide.vue';

const WIDGET_PACKAGE_NAME = 'Cricket Widget';

export default {
  name: 'LiveWidgetsConfiguration',
  components: {
    WidgetEndpoints,
    WidgetCredentials,
    WidgetIntegrationGuide,
  },
  data() {
    return {
      accessKey: '',
      savedAccessKey: '',
      endpoints: [''],
      savedEndpoints: [''],
      loading: false,
      initialLoading: true,
      error: '',
    };
  },
  computed: {
    ...mapState({
      activeProject: (state) => state.project.activeProject,
      packagesList: (state) => state.project.packagesList,
    }),
    activeApiKey() {
      return (this.activeProject
        && this.activeProject.active_api_keys
        && this.activeProject.active_api_keys.length)
        ? this.activeProject.active_api_keys[0].api_key
        : '';
    },
    hasWidgetPackage() {
      return this.packagesList.some(
        (pkg) => pkg.name === WIDGET_PACKAGE_NAME,
      );
    },
    packagesUrl() {
      const key = this.activeProject
        ? this.activeProject.key : '';
      return [
        Config.sportsHost,
        'user/project/',
        key,
        '/wizard?step=payment',
      ].join('');
    },
  },
  watch: {
    hasWidgetPackage(val) {
      if (val) {
        this.fetchExistingPublicKey();
      }
    },
  },
  mounted() {
    if (this.hasWidgetPackage) {
      this.fetchExistingPublicKey();
    }
  },
  methods: {
    cancelConfigure() {
      this.endpoints = [...this.savedEndpoints];
      this.accessKey = this.savedAccessKey;
      this.error = '';
    },
    async fetchExistingPublicKey() {
      if (!this.activeProject
        || !this.activeProject.key) {
        this.initialLoading = false;
        return;
      }
      try {
        const res = await axios.get(
          `${Config.widgetRtUrl}/widget-config/public-key`,
          {
            params: {
              projectKey: this.activeProject.key,
            },
          },
        );
        if (res.data && res.data.publicKey) {
          this.accessKey = res.data.publicKey;
          this.savedAccessKey = res.data.publicKey;
          const saved = res.data.endpoints
            || res.data.domains || [];
          if (saved.length > 0) {
            this.endpoints = [...saved];
            this.savedEndpoints = [...saved];
          }
        }
      } catch (err) {
        console.error(
          'Failed to fetch existing public key:', err,
        );
      } finally {
        this.initialLoading = false;
      }
    },
    generatePublicKey() {
      this.saveAndGenerate([]);
    },
    async saveAndGenerate(filteredEndpoints) {
      if (!this.activeProject
        || !this.activeProject.key) {
        this.error = 'No active project found';
        return;
      }
      if (!this.activeApiKey) {
        this.error = 'No API key found';
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        const res = await axios.post(
          `${Config.widgetRtUrl}/widget-config/generate-key`,
          {
            projectKey: this.activeProject.key,
            apiKey: this.activeApiKey,
            endpoints: filteredEndpoints,
          },
        );
        this.accessKey = res.data.publicKey;
        this.savedAccessKey = res.data.publicKey;
        this.savedEndpoints = [...this.endpoints];
      } catch (err) {
        console.error(
          'Failed to generate public key:', err,
        );
        this.error = err.response?.data?.error
          || 'Failed to generate public key';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.live-widgets-configuration {
  margin-bottom: size(50);

  h1 {
    @include font-styles(
      $type: 'display', $size: 'xs', $weight: 'bold'
    );
    margin-bottom: size(30);
  }

  .container {
    display: flex;
    justify-content: space-between;
    margin-bottom: size(50);

    .align-left {
      width: size(610);

      &:after {
        content: "";
        clear: both;
        display: block;
      }
    }

    .align-right {
      width: size(380);
    }
  }

  .generate-key-card {
    .configure-title {
      @include font-styles($type: 'text', $size: 'lg', $weight: 'bold');
      padding-bottom: size(14);
      display: inline-block;

      svg {
        display: inline-block;
        vertical-align: middle;
        width: size(23);
        height: size(21);
        margin-right: size(10);
      }
    }

    &__body {
      background: var(--global--white--Color);
      box-shadow: 0 size(1) size(3) rgba(0, 0, 0, 0.1),
        0 size(2) size(1) rgba(0, 0, 0, 0.06),
        0 size(1) size(1) rgba(0, 0, 0, 0.08);
      border-radius: size(8);
      padding: size(20);
      display: flex;
      flex-direction: column;
      gap: size(14);
    }

    &__desc {
      @include font-styles($type: 'label', $size: 'lg', $weight: 'regular');
      color: var(--global--main--Color);
      line-height: 1.6;
    }

    .error-message {
      color: var(--global--error--BackgroundColor);
      @include font-styles($type: 'label', $size: 'lg', $weight: 'regular');
    }
  }

  .no-access {
    &__card {
      display: flex;
      align-items: start;
      gap: size(40);
      background: var(--global--white--Color);
      box-shadow: 0 size(1) size(3) rgba(0, 0, 0, 0.1),
        0 size(2) size(1) rgba(0, 0, 0, 0.06),
        0 size(1) size(1) rgba(0, 0, 0, 0.08);
      border-radius: size(8);
      padding: size(40);
    }

    &__content {
      flex: 1;
    }

    &__heading {
      @include font-styles(
        $type: 'text', $size: 'lg', $weight: 'bold'
      );
      color: var(--global--main--title--Color);
      margin-bottom: size(14);
    }

    &__desc {
      @include font-styles(
        $type: 'label', $size: 'lg', $weight: 'regular'
      );
      color: var(--global--main--Color);
      line-height: 1.6;
      margin-bottom: size(24);
    }

    &__actions {
      display: flex;
      gap: size(12);
    }

    &__image {
      width: size(360);
      flex-shrink: 0;

      img {
        width: 100%;
        border-radius: size(8);
        display: block;
      }
    }
  }
}
</style>
